import { set as stateSet } from './state.js';
import { sharingSpinner } from './templates.js';
import { setGroupViMarkup, setPostVisibility, setViMarkup, toggleActivityPostFormPopup } from './ui.js';

/**
 * Controller for handling activity editing.
 */
export class ActivityEditController {
    constructor() {
        this.postFormDom = document.querySelector("#bp-nouveau-activity-form");
        this.initListeners();
    }

    initListeners() {
        const activityStreamEl = document.querySelector("#activity-stream");
        if (!activityStreamEl) return;

        activityStreamEl.addEventListener("click", (e) => {
            const editActivityBtn = e.target.closest(".edite-activity");
            if (editActivityBtn) {
                // FIX: Prevent event bubbling to document, which triggers the modal auto-close
                e.preventDefault();
                e.stopPropagation();

                this.handleActivityEditBtnClick(e);
            }
        });
    }

    async handleActivityEditBtnClick(e) {
        const item = e.target.closest(".activity-item");
        if (!item) return;

        const canEdit = item.dataset.canedite;

        if (canEdit == "false") return;

        // Open post form popup
        this.openPostFormPopup();

        // Get id of the activity
        const id = item.dataset.bpActivityId;
        // Use jQuery for data() retrieval if nonce is attached via jQuery data object, fallback to dataset
        const nonce = typeof jQuery !== 'undefined' ? jQuery(item).data('nonce') : item.dataset.nonce;

        try {
            await this.getActivity(id, nonce);
            // Set activity visibility
            this.setActivityVisibility();
            // Set activity group
            this.setActivityGroup();
            // Set activity content
            this.setActivityContent();
            // Set activity images
            this.setActivityImages();
            // Set data to edit activity
            this.setEditActivityData(e);
        } catch (error) {
            console.error('Failed to prepare activity for editing:', error);
            // If fetching failed, ensure we don't leave a broken spinner
            this.removeLoader();
        } finally {
            // Remove loader
            this.removeLoader();
        }
    }

    openPostFormPopup() {
        this.addLoader();
        // Change post activity btn text
        const submitBtn = this.postFormDom.querySelector("#aw-whats-new-submit");
        if (submitBtn) submitBtn.value = "Update Post";

        toggleActivityPostFormPopup("open");
    }

    setEditActivityData(e) {
        if (this.activity && this.activity.activities && this.activity.activities[0]) {
            stateSet('mf_activity_id_of_current_editeing_activity', this.activity.activities[0].id);
        }
        const el = e.target.closest(".activity-item");
        stateSet('mf_activity_current_editing_activity_dom', el);
    }

    addLoader() {
        const form = this.postFormDom.querySelector("#whats-new-form");
        if (!form) return;

        form.classList.add("submitting");
        const formEl = this.postFormDom.querySelector("form");
        if (formEl) formEl.insertAdjacentHTML("beforeend", sharingSpinner);
    }

    removeLoader() {
        const form = this.postFormDom.querySelector("#whats-new-form");
        if (!form) return;

        form.classList.remove("submitting");
        const spinner = this.postFormDom.querySelector(".sharing-spinner");
        if (spinner) spinner.remove();
    }

    async getActivity(id, nonce) {
        // We rely on jQuery.ajax here because the original theme logic likely uses global WP jQuery
        // for nonce/cookie handling automatically.
        const res = await jQuery.ajax({
            type: "post",
            url: window.Metafans_JS.ajaxurl,
            data: {
                activity_id: id,
                action: "mf_get_activity_by_id",
                _ajax_nonce: nonce,
            },
            success: (res) => {
                if (!res.success) {
                    this.removeLoader();
                    toggleActivityPostFormPopup("close");
                    if (typeof window.showNotification === 'function') {
                        window.showNotification('error', res.data.message || 'An error occurred.');
                    }
                    throw new Error(res.data.message);
                }
            }
        });
        this.activity = res.data;
    }

    setActivityVisibility() {
        const visibility = this.activity.activity_visibility;
        if (visibility && typeof visibility === 'object' && visibility.id) {
            setPostVisibility(this.postFormDom, visibility.id);
            setViMarkup(this.postFormDom, visibility.id);
        } else {
            // Fallback for string or unexpected format
            const viType = typeof visibility === 'string' ? visibility : 'public';
            setPostVisibility(this.postFormDom, viType);
            setViMarkup(this.postFormDom, viType);
        }
    }

    setActivityGroup() {
        if (!this.activity.activities || !this.activity.activities[0]) return;

        const isGroup = this.activity.activities[0].component;
        const objectSelect = this.postFormDom.querySelector(`#whats-new-post-object`);

        if (isGroup !== "groups" && isGroup === "activity") {
            if (objectSelect) objectSelect.value = isGroup;
            return;
        }

        const hiddenGroupEl = this.postFormDom.querySelector(
            `#whats-new-post-in option[value="${this.activity.activities[0].item_id}"]`
        );

        if (!hiddenGroupEl) return;

        // Add new selected el
        hiddenGroupEl.setAttribute("selected", "selected");
        // Add markup to post form to indicate group is selected
        setGroupViMarkup(this.postFormDom, hiddenGroupEl.innerText);
    }

    setActivityContent() {
        const contentEditable = this.postFormDom.querySelector(".advanced-th-bp-activity-form");
        const headingEl = this.postFormDom.querySelector(".activity-post-form-header h4");

        if (headingEl) headingEl.textContent = "Edit post";

        // Set content
        if (this.activity?.activities?.[0]) {
            let content = this.activity.activities[0].content;
            // Decode entities if needed or handle br tags
            content = content.replace(/\\/g, "").replace(/\n/g, "<br>");
            contentEditable.innerHTML = content;
        }

        // After content is set, handle preview logic
        const preview = this.postFormDom.querySelector(".whats-new-live-preview");
        if (preview) {
            const crossBtn = preview.querySelector(".cross");
            preview.setAttribute("contenteditable", "false");

            if (crossBtn) {
                // Ensure we don't bind multiple times if this is re-opened
                // Cloning allows stripping old listeners, or we assume a fresh render
                const newCross = crossBtn.cloneNode(true);
                crossBtn.parentNode.replaceChild(newCross, crossBtn);

                newCross.addEventListener("click", (e) => {
                    e.preventDefault();
                    const linkWrapper = preview.closest("a"); // Get the parent <a> tag
                    if (linkWrapper) {
                        linkWrapper.remove();
                    } else {
                        preview.remove();
                    }

                    // Optionally re-enable submit button
                    const submitBtn = this.postFormDom.querySelector("#aw-whats-new-submit");
                    if (submitBtn) {
                        submitBtn.removeAttribute("disabled");
                    }
                });
            }
        }
    }

    setActivityImages() {
        if (!this.activity.meta || !this.activity.meta.length) return;

        const markup = `${this.activity.meta.map((cur) => this.previewMarkup(cur)).join(" ")}`;

        // Add ids to hidden input so new image can save
        const mediaInput = this.postFormDom.querySelector("#whats-new-post-media");
        if (mediaInput) {
            mediaInput.value = this.activity.meta
                .map((cur) => cur.attachment_id)
                .join(", "); // Keep space after comma
        }

        const preCon = this.postFormDom.querySelector(".whats-new-previewer");
        if (preCon) {
            preCon.classList.add("previewer-toggle");
            preCon.insertAdjacentHTML("afterbegin", markup);
        }
    }

    previewMarkup(img) {
        const uid = Date.now() + Math.round(Math.random() * 20);
        const imgMediaMarkup = (url) => `<img src="${url}">`;

        // Safe check for attachment_id
        const attId = img.attachment_id ? img.attachment_id.trim() : '';

        return `<p class="media-uploading done image/png" id="${attId}">
          ${img.thumb ? imgMediaMarkup(img.thumb[0]) : ""}
           <span class="remove-media" data-media-id="${uid}"
           data-attachment-id="${attId}">x</span>
        </p>`;
    }
}